import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import Select2Dropdown from '../common/Select2Dropdown';
import { MultiSelectDropdown } from '../common/MultiSelectDropdown';
import JobForm from '../documentation/JobForm'; // Import JobForm
import '../documentation/JobList.css'; // Import styles for the modal
import './PPADReport.css';
import { statusOptions, statusesRequireRemarks } from '../../constants/constants';
import { usePermissions } from '../../services/PermissionsContext'; // 1. Import

const API_BASE_URL = '/api/documentation/jobs/'; // Changed to base jobs URL

const COLUMNS = [
  { key: 'sn', label: 'S/N' },
  { key: 'job_id', label: 'JOB ID' }, // Added JOB ID column
  { key: 'client_name', label: 'CLIENT NAME' },
  { key: 'email_subject', label: 'EMAIL SUBJECT' },
  { key: 'pending_status', label: 'PENDING STATUS' },
  { key: 'remarks', label: 'REMARKS' },
  { key: 'date', label: 'DATE' },
  { key: 'user', label: 'USER' }
];

function formatDateTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  const pad = n => n.toString().padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}\n${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

const toUpper = val => (val ? val.toString().toUpperCase() : '');

const IncompleteJobsReport = () => {
  const { hasPermission } = usePermissions(); // 2. Get the permission checker function
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterTerm, setFilterTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [showByUser, setShowByUser] = useState(true); // Default to true
  const [editRow, setEditRow] = useState(null);
  const [editStatus, setEditStatus] = useState([]);
  const [editRemarks, setEditRemarks] = useState('');

  // State for JobForm modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentJob, setCurrentJob] = useState(null);
  const [formMode, setFormMode] = useState('view');

  // When user filter changes (set or cleared), always turn on showByUser
  useEffect(() => {
    setShowByUser(true);
  }, [selectedUser]);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    const response = await fetch(`${API_BASE_URL}incomplete-jobs-report/`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
        'Content-Type': 'application/json'
      }
    });
    const result = await response.json();
    setJobs(result);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);


  // --- START: Functions to handle JobForm modal ---
  const handleViewJob = async (job) => {
    setIsModalOpen(false);
    setCurrentJob(null);
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}${job.id}/`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('kk_token')}` }
      });
      if (!res.ok) {
        throw new Error('Failed to load job details');
      }
      const fullJob = await res.json();
      setCurrentJob(fullJob);
      setFormMode('view');
      setIsModalOpen(true);
    } catch (err) {
      console.error('Error fetching job details', err);
      alert('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const handleFormClose = (refresh = false) => {
    setIsModalOpen(false);
    setCurrentJob(null);
    if (refresh) {
      fetchJobs(); // Refresh the report if the job was updated
    }
  };
  // --- END: Functions to handle JobForm modal ---

  // Filter logic: search all columns, case-insensitive, and by user
  const filteredJobs = jobs.filter(job => {
    if (selectedUser && job.user !== selectedUser) return false;
    if (!filterTerm) return true;
    const term = filterTerm.toUpperCase();
    return (
      toUpper((jobs.indexOf(job) + 1)).includes(term) ||
      toUpper(job.client_name).includes(term) ||
      toUpper(job.email_subject).includes(term) ||
      toUpper(job.pending_status).includes(term) ||
      toUpper(job.remarks).includes(term) ||
      toUpper(formatDateTime(job.date)).includes(term) ||
      toUpper(job.user).includes(term)
    );
  });

  // Group jobs by user for hierarchical display
  const jobsByUser = filteredJobs.reduce((acc, job) => {
    const user = job.user || 'Unknown User';
    if (!acc[user]) acc[user] = [];
    acc[user].push(job);
    return acc;
  }, {});

  // Handle status/remarks update
  const handleEdit = (job) => {
    setEditRow(job.id);
    if (Array.isArray(job.pending_status)) {
      setEditStatus(job.pending_status);
    } else if (typeof job.pending_status === 'string') {
      setEditStatus(
        job.pending_status
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
      );
    } else {
      setEditStatus([]);
    }
    setEditRemarks(job.remarks);
  };

  const handleSave = async (job) => {
    // 3. Add a permission check before saving
    if (!hasPermission('INCOMPLETE JOBS', 'can_edit')) {
        alert("You do not have permission to perform this action.");
        return;
    }

    const needsRemarks = editStatus.some(opt =>
      statusesRequireRemarks.some(req => opt.toUpperCase().includes(req))
    );
    if (needsRemarks && (!editRemarks || editRemarks.trim() === '')) {
      alert('Remarks are required for the selected status.');
      return;
    }
    const response = await fetch(`/api/documentation/jobs/${job.id}/update-status/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('kk_token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        status: editStatus,
        remarks: editRemarks
      })
    });
    if (response.ok) {
      setEditRow(null);
      fetchJobs();
    } else {
      alert('Failed to update status.');
    }
  };

  const handleCancel = () => {
    setEditRow(null);
    setEditStatus([]);
    setEditRemarks('');
  };

  const exportExcel = () => {
    const exportData = filteredJobs.map((job, idx) => ({
      'S/N': (idx + 1).toString().toUpperCase(),
      'CLIENT NAME': toUpper(job.client_name),
      'EMAIL SUBJECT': toUpper(job.email_subject),
      'PENDING STATUS': Array.isArray(job.pending_status)
        ? job.pending_status.join('\n')
        : (job.pending_status || '')
            .split(',')
            .map(item => item.trim())
            .filter(Boolean)
            .map(item => toUpper(item))
            .join('\n'),
      'REMARKS': toUpper(job.remarks),
      'DATE': toUpper(formatDateTime(job.date)),
      'USER': toUpper(job.user)
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "INCOMPLETE JOBS REPORT");
    XLSX.writeFile(wb, "INCOMPLETE_JOBS_REPORT.xlsx");
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.text("INCOMPLETE JOBS REPORT", 14, 10);
    const body = filteredJobs.map((job, idx) => [
      toUpper(idx + 1),
      toUpper(job.client_name),
      toUpper(job.email_subject),
      Array.isArray(job.pending_status)
        ? job.pending_status.join('\n')
        : (job.pending_status || '')
            .split(',')
            .map(item => item.trim())
            .filter(Boolean)
            .map(item => toUpper(item))
            .join('\n'),
      toUpper(job.remarks),
      toUpper(formatDateTime(job.date)),
      toUpper(job.user)
    ]);
    doc.autoTable({
      head: [COLUMNS.map(col => col.label)],
      body,
      startY: 18,
      styles: { fontSize: 8, textTransform: 'uppercase', cellPadding: 2 },
      columnStyles: {
        3: { cellWidth: 50 },
      }
    });
    doc.save("INCOMPLETE_JOBS_REPORT.pdf");
  };

  // User dropdown options from jobs data
  const userOptions = useMemo(() => Array.from(
    new Set(jobs.map(j => j.user).filter(Boolean))
  ).map(username => ({
    id: username,
    text: username
  })), [jobs]);

  // Width for user dropdown — enough to show the longest username without truncation
  const userDropdownWidth = useMemo(() => {
    const longestLen = Math.max(
      'USER'.length,
      ...userOptions.map(u => u.text.length)
    );
    return longestLen * 8 + 52; // ~8px per char + padding + arrow
  }, [userOptions]);

  // 4. Add a top-level permission check for viewing the report
  if (!hasPermission('INCOMPLETE JOBS', 'can_view')) {
    return (
        <div className="ppad-report-container">
            <h2>Permission Denied</h2>
            <p>You do not have permission to view this report.</p>
        </div>
    );
  }

  return (
    <div className="ppad-report-container" style={{ overflowX: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <h2 style={{ textTransform: 'uppercase', margin: 0 }}>INCOMPLETE JOBS REPORT</h2>
        <button onClick={fetchJobs} title="Refresh" style={{ background: 'none', border: '1px solid transparent', borderRadius: 6, cursor: 'pointer', fontSize: 18, color: '#444', padding: '4px 8px', transition: 'background 0.15s, border-color 0.15s' }} onMouseEnter={e => { e.currentTarget.style.background = '#f0f0f0'; e.currentTarget.style.borderColor = '#bbb'; }} onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}><i className="fa-solid fa-rotate-right" style={{ fontWeight: 900 }} /></button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {/* Left: checkbox + search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <label htmlFor="displayByUser" style={{ display: 'inline-flex', alignItems: 'center', margin: 0, padding: 0, cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', gap: 0 }}>
            <input
              type="checkbox"
              checked={showByUser}
              onChange={e => setShowByUser(e.target.checked)}
              id="displayByUser"
              style={{ appearance: 'auto', width: 13, height: 13, margin: 0, marginRight: 3, padding: 0, flexShrink: 0, cursor: 'pointer', accentColor: '#007bff', verticalAlign: 'middle' }}
            />
            Display by User
          </label>
          <input
            type="text"
            placeholder="SEARCH"
            value={filterTerm}
            onChange={e => setFilterTerm(e.target.value)}
            style={{ width: 200, fontSize: 13, padding: '4px 8px', border: '1px solid #ccc', borderRadius: 4 }}
          />
        </div>
        {/* Center: user summary cards */}
        {(() => {
          const cardColors = [
            { bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' },
            { bg: '#dcfce7', border: '#86efac', text: '#166534' },
            { bg: '#fef9c3', border: '#fde047', text: '#854d0e' },
            { bg: '#fce7f3', border: '#f9a8d4', text: '#9d174d' },
            { bg: '#ede9fe', border: '#c4b5fd', text: '#5b21b6' },
            { bg: '#ffedd5', border: '#fdba74', text: '#9a3412' },
            { bg: '#cffafe', border: '#67e8f9', text: '#155e75' },
            { bg: '#f0fdf4', border: '#4ade80', text: '#14532d' },
          ];
          const countsByUser = jobs.reduce((acc, job) => {
            const u = job.user || 'Unknown';
            acc[u] = (acc[u] || 0) + 1;
            return acc;
          }, {});
          const users = Object.keys(countsByUser).sort();
          if (users.length === 0) return null;
          const totalCount = jobs.length;
          const isTotalActive = !selectedUser;
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', flex: 1, padding: '0 8px' }}>
              {/* Total card */}
              <button
                onClick={() => setSelectedUser('')}
                style={{
                  background: isTotalActive ? '#374151' : '#f3f4f6',
                  border: '2px solid #9ca3af',
                  borderRadius: 8,
                  padding: '6px 14px',
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  minWidth: 70,
                  transition: 'background 0.15s',
                  outline: isTotalActive ? '2px solid #374151' : 'none',
                  outlineOffset: 2,
                }}
              >
                <span style={{ fontSize: 10, color: isTotalActive ? '#e0e0e0' : '#374151', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>ALL</span>
                <span style={{ fontWeight: 'bold', fontSize: 14, color: isTotalActive ? '#fff' : '#374151', lineHeight: 1 }}>{totalCount}</span>
              </button>
              {users.map((user, i) => {
                const c = cardColors[i % cardColors.length];
                const isActive = selectedUser === user;
                return (
                  <button
                    key={user}
                    onClick={() => setSelectedUser(isActive ? '' : user)}
                    style={{
                      background: isActive ? c.text : c.bg,
                      border: `2px solid ${c.border}`,
                      borderRadius: 8,
                      padding: '6px 14px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                      minWidth: 70,
                      transition: 'background 0.15s, color 0.15s',
                      outline: isActive ? `2px solid ${c.text}` : 'none',
                      outlineOffset: 2,
                    }}
                  >
                    <span style={{ fontSize: 10, color: isActive ? '#e0e0e0' : c.text, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                      {user}
                    </span>
                    <span style={{ fontWeight: 'bold', fontSize: 14, color: isActive ? '#fff' : c.text, lineHeight: 1 }}>
                      {countsByUser[user]}
                    </span>
                  </button>
                );
              })}
            </div>
          );
        })()}
        {/* Right: export buttons */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="dialog-btn dialog-btn-primary" onClick={exportExcel}>EXPORT EXCEL</button>
          <button className="dialog-btn dialog-btn-primary" onClick={exportPDF}>EXPORT PDF</button>
        </div>
      </div>
      {loading ? (
        <p>LOADING...</p>
      ) : (
        <div>

          <div
            style={{
              fontWeight: 'bold',
              marginBottom: 8,
              background: '#f59393ff',
              borderRadius: 6,
              padding: 8,
              fontSize: 16,
              textTransform: 'uppercase'
            }}
          >
            INCOMPLETE JOBS
          </div>
          {/* Hierarchy view by user */}
          {(showByUser || selectedUser) ? (
            Object.keys(jobsByUser).map(user => (
              <div key={user} style={{ marginBottom: 24 }}>
                <div style={{ fontWeight: 'bold', fontSize: 15, margin: '12px 0 6px 0', color: '#007bff', textTransform: 'uppercase' }}>
                  {user}
                </div>
                <div>
                <table style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse', overflow: 'visible' }}>
                  <thead>
                    <tr>
                      {COLUMNS.filter(col => col.key !== 'user').map(col => (
                        <th key={col.key} style={{ textTransform: 'uppercase', whiteSpace: col.key === 'email_subject' ? 'normal' : 'nowrap' }}>{col.label}</th>
                      ))}
                      {/* 5. Conditionally render the Actions column header */}
                      {hasPermission('INCOMPLETE JOBS', 'can_edit') && (
                        <th style={{ textTransform: 'uppercase', whiteSpace: 'nowrap' }}>ACTIONS</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {jobsByUser[user].map((job, idx) => (
                      <tr key={job.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{toUpper(idx + 1)}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <a href="#" onClick={(e) => { e.preventDefault(); handleViewJob(job); }}>
                            {job.id}
                          </a>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}><strong>{toUpper(job.client_name)}</strong></td>
                        <td style={{ whiteSpace: 'normal', wordBreak: 'break-word', minWidth: 160 }}>{toUpper(job.email_subject)}</td>
                        <td>
                          {editRow === job.id ? (
                            <MultiSelectDropdown
                              options={statusOptions}
                              selectedValues={editStatus}
                              onChange={vals => setEditStatus(vals)}
                              placeholder="Select Status..."
                              disabled={!hasPermission('INCOMPLETE JOBS', 'can_edit')}
                              id={`status-edit-${job.id}`}
                            />
                          ) : (
                            <ul style={{ margin: 0, paddingLeft: 18, textTransform: 'uppercase', listStyleType: 'disc' }}>
                              {(Array.isArray(job.pending_status) ? job.pending_status : (job.pending_status || '').split(','))
                                .map(item => item.trim())
                                .filter(Boolean)
                                .map((item, i) => (
                                  <li key={i}>{item}</li>
                                ))}
                            </ul>
                          )}
                        </td>
                        <td>
                          {editRow === job.id ? (
                            <input
                              type="text"
                              value={editRemarks}
                              onChange={e => setEditRemarks(e.target.value)}
                              required={editStatus.some(opt =>
                                statusesRequireRemarks.some(req => opt.toUpperCase().includes(req))
                              )}
                              style={{ minWidth: 120 }}
                              readOnly={!hasPermission('INCOMPLETE JOBS', 'can_edit')}
                            />
                          ) : (
                            toUpper(job.remarks)
                          )}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {(() => {
                            const dt = formatDateTime(job.date).split('\n');
                            return (
                              <span>
                                {dt[0]}
                                <br />
                                <span style={{ fontSize: 11, color: '#555' }}>{dt[1]}</span>
                              </span>
                            );
                          })()}
                        </td>
                        {/* 6. Conditionally render the Actions cell */}
                        {hasPermission('INCOMPLETE JOBS', 'can_edit') && (
                          <td>
                            {editRow === job.id ? (
                              <>
                                <button className="icon-btn save-btn" onClick={() => handleSave(job)} title="Save">
                                  <i className="fa fa-save"></i>
                                </button>
                                <button className="icon-btn cancel-btn" onClick={handleCancel} title="Cancel">
                                  <i className="fa fa-times"></i>
                                </button>
                              </>
                            ) : (
                              <button className="icon-btn edit-btn" onClick={() => handleEdit(job)} title="Edit">
                                <i className="fa fa-pencil-alt"></i>
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            ))
          ) : (
            <div>
            <table style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse', overflow: 'visible' }}>
              <thead>
                <tr>
                  {COLUMNS.map(col =>
                    (col.key !== 'user' || !showByUser) && (
                      <th key={col.key} style={{ textTransform: 'uppercase', whiteSpace: col.key === 'email_subject' ? 'normal' : 'nowrap' }}>{col.label}</th>
                    )
                  )}
                  {/* 7. Conditionally render the Actions column header in the flat view */}
                  {hasPermission('INCOMPLETE JOBS', 'can_edit') && (
                    <th style={{ textTransform: 'uppercase', whiteSpace: 'nowrap' }}>ACTIONS</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredJobs.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length + 1} style={{ textTransform: 'uppercase' }}>NO INCOMPLETE JOBS FOUND.</td>
                  </tr>
                ) : (
                  filteredJobs.map((job, idx) => (
                    <tr key={job.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{toUpper(idx + 1)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <a href="#" onClick={(e) => { e.preventDefault(); handleViewJob(job); }}>
                          {job.id}
                        </a>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{toUpper(job.client_name)}</td>
                      <td style={{ whiteSpace: 'normal', wordBreak: 'break-word', minWidth: 160 }}>{toUpper(job.email_subject)}</td>
                      <td>
                        {editRow === job.id ? (
                          <MultiSelectDropdown
                            options={statusOptions}
                            selectedValues={editStatus}
                            onChange={vals => setEditStatus(vals)}
                            placeholder="Select Status..."
                            disabled={!hasPermission('INCOMPLETE JOBS', 'can_edit')}
                            id={`status-edit-${job.id}`}
                          />
                        ) : (
                          <ul style={{ margin: 0, paddingLeft: 18, textTransform: 'uppercase', listStyleType: 'disc' }}>
                            {(Array.isArray(job.pending_status) ? job.pending_status : (job.pending_status || '').split(','))
                              .map(item => item.trim())
                              .filter(Boolean)
                              .map((item, i) => (
                                <li key={i}>{item}</li>
                              ))}
                          </ul>
                        )}
                      </td>
                      <td>
                        {editRow === job.id ? (
                          <input
                            type="text"
                            value={editRemarks}
                            onChange={e => setEditRemarks(e.target.value)}
                            required={editStatus.some(opt =>
                              statusesRequireRemarks.some(req => opt.toUpperCase().includes(req))
                            )}
                            style={{ minWidth: 120 }}
                            readOnly={!hasPermission('INCOMPLETE JOBS', 'can_edit')}
                          />
                        ) : (
                          toUpper(job.remarks)
                        )}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {(() => {
                          const dt = formatDateTime(job.date).split('\n');
                          return (
                            <span>
                              {dt[0]}
                              <br />
                              <span style={{ fontSize: 11, color: '#555' }}>{dt[1]}</span>
                            </span>
                          );
                        })()}
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>{toUpper(job.user)}</td>
                      {/* 8. Conditionally render the Actions cell in the flat view */}
                      {hasPermission('INCOMPLETE JOBS', 'can_edit') && (
                        <td>
                          {editRow === job.id ? (
                            <>
                              <button className="icon-btn save-btn" onClick={() => handleSave(job)} title="Save">
                                <i className="fa fa-save"></i>
                              </button>
                              <button className="icon-btn cancel-btn" onClick={handleCancel} title="Cancel">
                                <i className="fa fa-times"></i>
                              </button>
                            </>
                          ) : (
                            <button className="icon-btn edit-btn" onClick={() => handleEdit(job)} title="Edit">
                              <i className="fa fa-pencil-alt"></i>
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>
          )}
        </div>
      )}
      {/* --- START: JobForm Modal --- */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="modal-close-btn" onClick={() => handleFormClose(false)}>
              &times;
            </button>
            <JobForm
              mode={formMode}
              initialData={currentJob}
              onClose={handleFormClose}
            />
          </div>
        </div>
      )}
      {/* --- END: JobForm Modal --- */}
    </div>
  );
};

export default IncompleteJobsReport;